import { createAsyncThunk } from '@reduxjs/toolkit';
import { upsellingApi } from './upsellingApi';
import { summarizeAssignment } from './upsellingConstants';
import { extractApiError } from '../../utils/errors';

export const fetchIncomingUpsells = createAsyncThunk(
  'upselling/fetchIncoming',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().upselling;
      const data = await upsellingApi.getIncomingUpsells({
        page: filters.page,
        page_size: filters.rowsPerPage,
      });
      return {
        results: data.results || [],
        count: data.count || 0,
      };
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener las solicitudes entrantes'));
    }
  }
);

/**
 * Just the pending total, for the badge in the Deliveries menu.
 * Asks for a single row so the count costs as little as possible, and keeps the
 * inbox list untouched.
 */
export const fetchPendingUpsellsCount = createAsyncThunk(
  'upselling/fetchPendingCount',
  async (_, { rejectWithValue }) => {
    try {
      const data = await upsellingApi.getIncomingUpsells({ page: 1, page_size: 1 });
      return data.count || 0;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo obtener el conteo de solicitudes'));
    }
  }
);

export const manageUpsellRequests = createAsyncThunk(
  'upselling/manageRequests',
  async ({ orderIds, action }, { dispatch, rejectWithValue }) => {
    try {
      const data = await upsellingApi.manageUpsellRequests({
        order_ids: orderIds,
        action,
      });
      // Awaited, so the confirmation keeps spinning until the list no longer
      // offers the requests it just processed.
      await Promise.all([dispatch(fetchIncomingUpsells()), dispatch(fetchPendingUpsellsCount())]);
      return data?.details?.processed_count ?? orderIds.length;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo procesar la solicitud'));
    }
  }
);

export const assignDeliveriesToPartner = createAsyncThunk(
  'upselling/assign',
  async ({ orderIds, partnershipId }, { rejectWithValue }) => {
    try {
      const data = await upsellingApi.assignDeliveries({
        order_ids: orderIds,
        partnership_id: partnershipId,
      });
      return summarizeAssignment(data, orderIds.length);
    } catch (error) {
      // A 400 from this endpoint is not an error to report as one: it carries
      // the same body as a 200 and only means no order made it through. The
      // per-order reasons are the useful part, so it resolves like any other
      // outcome. A 403 also sends a `summary`, but one holding serializer
      // errors rather than counts — hence the check on `total_requested`.
      const body = error.response?.data;
      if (body?.summary?.total_requested !== undefined) {
        return summarizeAssignment(body, orderIds.length);
      }
      return rejectWithValue(extractApiError(error, 'No se pudieron ceder los envíos'));
    }
  }
);
